/**
 * Cargar los costos: el momento en que el tecnico contesta.
 *
 * Dos numeros y nada mas: lo que cobra el tecnico y lo que se queda el local.
 * El precio al cliente se calcula abajo, en grande, y no se puede tipear: es
 * la suma. La ganancia viene precargada con la regla del negocio (los 20
 * dolares), asi que en el caso tipico lo unico que se escribe es lo que dijo
 * el tecnico.
 *
 * Se puede volver a abrir para corregir mientras la orden no este facturada.
 */
import { useEffect, useMemo, useState } from "react";
import type { Moneda } from "@tora/shared";

import { Importe, InputImporte } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Segmented } from "@/components/ui/segmented";
import { Textarea } from "@/components/ui/textarea";
import type { CostosOt } from "@/lib/tipos";

import {
  armarCostos,
  cambiarMonedaCostos,
  costosIniciales,
  esInterna,
  nombreOrden,
  precioDeCostos,
  validarCostos,
  type FormularioCostos,
  type OrdenTaller,
} from "./logica";

const MONEDAS: ReadonlyArray<{ valor: Moneda; etiqueta: string }> = [
  { valor: "USD", etiqueta: "Dólares" },
  { valor: "ARS", etiqueta: "Pesos" },
];

export interface DialogCostosProps {
  orden: OrdenTaller | null;
  /** La regla del negocio: lo que se precarga como ganancia. */
  regla: { ganancia: number };
  /** El dolar del dia: con el pasa la ganancia de la regla a pesos. */
  cotizacion?: number;
  guardando?: boolean;
  onCerrar: () => void;
  onConfirmar: (costos: CostosOt) => void;
}

export function DialogCostos({
  orden,
  regla,
  cotizacion = 0,
  guardando = false,
  onCerrar,
  onConfirmar,
}: DialogCostosProps): JSX.Element {
  const [form, setForm] = useState<FormularioCostos | null>(null);
  const [intentado, setIntentado] = useState(false);

  useEffect(() => {
    setForm(orden ? costosIniciales(orden, regla) : null);
    setIntentado(false);
  }, [orden, regla]);

  const interna = orden ? esInterna(orden) : false;
  const validacion = useMemo(
    () => (form ? validarCostos(form, interna) : { ok: false }),
    [form, interna],
  );
  const precio = form ? precioDeCostos(form) : 0;
  const corrigiendo = Boolean(orden?.costos_at);

  function cambiar<K extends keyof FormularioCostos>(campo: K, valor: FormularioCostos[K]): void {
    setForm((prev) => (prev ? { ...prev, [campo]: valor } : prev));
  }

  function confirmar(): void {
    setIntentado(true);
    if (!form || !validacion.ok) return;
    onConfirmar(armarCostos(form));
  }

  return (
    <Dialog open={orden !== null} onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent tamano="md">
        {orden && form ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {interna
                  ? corrigiendo
                    ? "Corregir el costo"
                    : "Cargar el costo"
                  : corrigiendo
                    ? "Corregir el presupuesto"
                    : "Cargar costo y presupuesto"}
              </DialogTitle>
              <DialogDescription>
                {nombreOrden(orden)} · {orden.equipo}.{" "}
                {interna
                  ? "Lo que cobra el técnico. Se suma al costo del equipo cuando vuelve."
                  : "Lo que contestó el técnico, y lo que se cobra."}
              </DialogDescription>
            </DialogHeader>

            <DialogBody>
              <Card
                variant="muted"
                padding="compacto"
                className="flex items-center justify-between gap-4"
              >
                <span className="text-xs text-muted-foreground">Los montos van en</span>
                <Segmented
                  opciones={MONEDAS}
                  valor={form.moneda}
                  onChange={(m) =>
                    setForm((prev) =>
                      prev ? cambiarMonedaCostos(prev, m, regla, cotizacion) : prev,
                    )
                  }
                  etiqueta="Moneda de los costos"
                  apariencia="segmentado"
                  tamano="sm"
                />
              </Card>

              <InputImporte
                label="Costo del técnico"
                valor={form.costoTecnico}
                onValorChange={(v) => cambiar("costoTecnico", v)}
                moneda={form.moneda}
                autoFocus
                requerido
                ayuda="Lo que cobra el servicio técnico por el trabajo. Es lo único que no se adivina."
              />

              {!interna ? (
                <InputImporte
                  label="Ganancia"
                  valor={form.ganancia}
                  onValorChange={(v) => cambiar("ganancia", v)}
                  moneda={form.moneda}
                  ayuda="Lo que se queda el local. Se puede negociar."
                />
              ) : null}

              <Card
                variant="muted"
                padding="compacto"
                className="flex items-baseline justify-between gap-4"
              >
                <span className="text-sm font-medium">
                  {interna ? "Suma al costo del equipo" : "Precio al cliente"}
                </span>
                <Importe
                  monto={precio}
                  moneda={form.moneda}
                  tamano="lg"
                  className="font-semibold"
                />
              </Card>

              <Campo label="Qué encontró el técnico">
                <Textarea
                  id="ot-diagnostico"
                  rows={2}
                  value={form.diagnostico}
                  onChange={(e) => cambiar("diagnostico", e.target.value)}
                  placeholder="Pin de carga dañado, hay que cambiar el flex…"
                />
              </Campo>

              {intentado && !validacion.ok && "problema" in validacion ? (
                <Aviso tono="destructive">{validacion.problema}</Aviso>
              ) : null}
            </DialogBody>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onCerrar}>
                Cancelar
              </Button>
              <Button type="button" cargando={guardando} onClick={confirmar}>
                {interna
                  ? corrigiendo
                    ? "Guardar el costo"
                    : "Cargar el costo"
                  : corrigiendo
                    ? "Guardar el presupuesto"
                    : "Cargar costo y presupuesto"}
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
